import React, { useCallback, useEffect, useState } from "react";

/**
 * Locale-proof dd/mm/yyyy date input used across the entire Viyora app.
 *
 * Why not `<input type="date">`?
 *   Native date pickers follow the OS / browser locale — Chrome on `ENG-IN`
 *   still renders `mm/dd/yyyy`, causing clinics in India / UK / EU to see the
 *   wrong format. There is NO cross-browser flag to force `dd/mm/yyyy` on the
 *   native picker. So we render a masked text field ourselves and keep the
 *   wire format identical to the old input (ISO `yyyy-mm-dd`).
 *
 * Contract (drop-in replacement for `<input type="date">`):
 *   - `value`   : ISO `"YYYY-MM-DD"` (or empty string)
 *   - `onChange`: called with a synthetic event `{ target: { value: iso } }`
 *                 — this preserves every existing `onChange={(e) => ...}`
 *                 handler in the codebase, no rewriting required.
 *   - `min` / `max` : optional ISO bounds (`"YYYY-MM-DD"`).
 *   - Every other prop (className, disabled, required, data-testid, name,
 *     placeholder…) is forwarded to the underlying <input>.
 */

// ---------- helpers ----------
export function isoToDMY(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}
export function dmyToISO(dmy) {
  if (!dmy || typeof dmy !== "string") return "";
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dmy.trim());
  if (!m) return "";
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const y = parseInt(m[3], 10);
  if (!d || !mo || !y || mo > 12 || d > 31 || y < 1900 || y > 2200) return "";
  // Validate the day against the actual month length (catches 31/02, 30/02…).
  const testDate = new Date(Date.UTC(y, mo - 1, d));
  if (testDate.getUTCFullYear() !== y || testDate.getUTCMonth() !== mo - 1 || testDate.getUTCDate() !== d) return "";
  return `${y.toString().padStart(4, "0")}-${mo.toString().padStart(2, "0")}-${d.toString().padStart(2, "0")}`;
}

function _autoFormat(raw) {
  // Keep only digits and insert slashes progressively.
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

// ---------- component ----------
export function DateDMYInput({
  value,
  onChange,
  min,
  max,
  className = "",
  placeholder = "dd/mm/yyyy",
  disabled = false,
  ...rest
}) {
  // Draft is what the user sees / types; `value` (from parent) is ISO.
  const [draft, setDraft] = useState(() => isoToDMY(value || ""));

  useEffect(() => {
    // Reflect controlled changes from the parent — but don't clobber the
    // draft while the user is actively typing a partial value.
    const nextDMY = isoToDMY(value || "");
    const partialMatchesIso = dmyToISO(draft) === (value || "");
    if (!partialMatchesIso) setDraft(nextDMY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = useCallback((iso) => {
    if (typeof onChange !== "function") return;
    onChange({ target: { value: iso } });
  }, [onChange]);

  const withinBounds = (iso) => {
    if (!iso) return true;
    if (min && iso < min) return false;
    if (max && iso > max) return false;
    return true;
  };

  const handleChange = (e) => {
    const next = _autoFormat(e.target.value);
    setDraft(next);
    const iso = dmyToISO(next);
    if (iso && withinBounds(iso)) {
      emit(iso);
    } else if (next === "") {
      emit("");
    }
    // Otherwise: wait for a valid parse (blur / more digits).
  };

  const handleBlur = () => {
    if (!draft) { emit(""); return; }
    const iso = dmyToISO(draft);
    if (!iso || !withinBounds(iso)) {
      // Invalid → clear silently so the user can retry.
      setDraft(isoToDMY(value || ""));
      return;
    }
    // Normalise pretty (auto-pad single digits typed in raw).
    setDraft(isoToDMY(iso));
    emit(iso);
  };

  const handleKeyDown = (e) => {
    // Escape clears; Ctrl+; inserts today (matches native <DateDMYInput>
    // convention on some Linux/Chrome builds — nice power-user shortcut).
    if (e.key === "Escape") { setDraft(""); emit(""); }
    if (e.ctrlKey && e.key === ";") {
      e.preventDefault();
      const t = new Date();
      const iso = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
      setDraft(isoToDMY(iso));
      emit(iso);
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      spellCheck={false}
      maxLength={10}
      pattern="\d{2}/\d{2}/\d{4}"
      placeholder={placeholder}
      disabled={disabled}
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
      aria-label={rest["aria-label"] || "Date (dd/mm/yyyy)"}
      {...rest}
    />
  );
}

export default DateDMYInput;
